/**
 * Office Dialog API wrapper for authentication flows
 * Provides a unified interface for both Office desktop and browser environments
 */
export class OfficeDialogUtils {
    /**
     * Determines if the code is running in an Office desktop application
     * (as opposed to Office Online in a browser)
     */
    static isOfficeDesktop() {
        try {
            console.log('[OfficeDialogUtils] isOfficeDesktop check starting...');
            if (typeof Office === 'undefined' || !Office.context) {
                console.log('[OfficeDialogUtils] Office or Office.context not available');
                return false;
            }
            // Check if we're in a desktop platform
            const platform = Office.context.platform;
            console.log('[OfficeDialogUtils] Platform detected:', platform);
            const isDesktop = platform === Office.PlatformType.PC || platform === Office.PlatformType.Mac;
            console.log('[OfficeDialogUtils] isOfficeDesktop result:', isDesktop);
            return isDesktop;
        }
        catch (error) {
            console.error('[OfficeDialogUtils] Failed to detect Office platform:', error);
            return false;
        }
    }
    /**
     * Determines if the code is running inside an Office Dialog
     */
    static isInOfficeDialog() {
        try {
            console.log('[OfficeDialogUtils] isInOfficeDialog check starting...');
            console.log('[OfficeDialogUtils] Office defined:', typeof Office !== 'undefined');
            if (typeof Office === 'undefined') {
                console.log('[OfficeDialogUtils] Office is undefined');
                return false;
            }
            console.log('[OfficeDialogUtils] Office.context:', !!Office.context);
            console.log('[OfficeDialogUtils] Office.context.ui:', !!(Office.context && Office.context.ui));
            if (Office.context && Office.context.ui) {
                console.log('[OfficeDialogUtils] messageParent type:', typeof Office.context.ui.messageParent);
            }
            const result = typeof Office !== 'undefined' &&
                Office.context &&
                Office.context.ui &&
                typeof Office.context.ui.messageParent === 'function';
            console.log('[OfficeDialogUtils] isInOfficeDialog result:', result);
            return result;
        }
        catch (error) {
            console.error('[OfficeDialogUtils] isInOfficeDialog error:', error);
            return false;
        }
    }
    /**
     * Opens an authentication dialog using Office.js Dialog API
     * @param url The URL to open in the dialog
     * @param width Dialog width percentage (default 60)
     * @param height Dialog height percentage (default 60)
     * @returns Promise that resolves with authentication data or rejects on error
     */
    static openDialog(url, width = 60, height = 60) {
        console.log('[OfficeDialogUtils] openDialog called with URL:', url);
        console.log('[OfficeDialogUtils] Dialog dimensions:', { width, height });
        return new Promise((resolve, reject) => {
            try {
                console.log('[OfficeDialogUtils] Calling displayDialogAsync...');
                Office.context.ui.displayDialogAsync(url, {
                    height,
                    width,
                    promptBeforeOpen: false,
                }, (asyncResult) => {
                    console.log('[OfficeDialogUtils] displayDialogAsync callback received');
                    console.log('[OfficeDialogUtils] asyncResult.status:', asyncResult.status);
                    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                        console.error('[OfficeDialogUtils] Dialog failed to open:', asyncResult.error);
                        reject(new Error(`Dialog error: ${asyncResult.error.message}`));
                        return;
                    }
                    const dialog = asyncResult.value;
                    console.log('[OfficeDialogUtils] Dialog opened successfully, setting up event handlers...');
                    // Handle messages from the dialog
                    dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
                        console.log('[OfficeDialogUtils] DialogMessageReceived event fired');
                        console.log('[OfficeDialogUtils] Raw message:', arg.message);
                        try {
                            const messageData = JSON.parse(arg.message);
                            console.log('[OfficeDialogUtils] Parsed message data:', {
                                source: messageData.source,
                                hasUser: !!messageData.user,
                                userLength: messageData.user?.length,
                            });
                            if (messageData.source === 'oidc-client' && messageData.user) {
                                console.log('[OfficeDialogUtils] Valid auth message received, resolving and closing dialog...');
                                resolve(messageData);
                                dialog.close();
                                console.log('[OfficeDialogUtils] Dialog close called');
                            }
                            else {
                                console.warn('[OfficeDialogUtils] Message received but not valid auth message');
                            }
                        }
                        catch (error) {
                            console.error('[OfficeDialogUtils] Failed to parse dialog message:', error);
                            reject(error);
                            dialog.close();
                        }
                    });
                    console.log('[OfficeDialogUtils] DialogMessageReceived handler registered');
                    // Handle dialog closed by user
                    dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
                        console.log('[OfficeDialogUtils] DialogEventReceived event fired:', arg);
                        if (arg.error === 12006) {
                            // Dialog closed by user
                            console.log('[OfficeDialogUtils] Dialog closed by user (error 12006)');
                            reject(new Error('Authentication cancelled by user'));
                        }
                        else {
                            console.error('[OfficeDialogUtils] Dialog event error:', arg.error);
                            reject(new Error(`Dialog error: ${arg.error}`));
                        }
                    });
                    console.log('[OfficeDialogUtils] DialogEventReceived handler registered');
                });
            }
            catch (error) {
                console.error('[OfficeDialogUtils] Failed to open dialog:', error);
                reject(error);
            }
        });
    }
    /**
     * Sends a message from the dialog back to the parent taskpane
     * Should be called from within the dialog window
     * @param data The data to send to the parent
     */
    static sendMessageToParent(data) {
        console.log('[OfficeDialogUtils] sendMessageToParent called');
        console.log('[OfficeDialogUtils] Data to send:', {
            source: data.source,
            hasUser: !!data.user,
        });
        if (!this.isInOfficeDialog()) {
            console.error('[OfficeDialogUtils] Not in Office Dialog context, cannot send message');
            return;
        }
        try {
            const message = JSON.stringify(data);
            console.log('[OfficeDialogUtils] Message serialized, length:', message.length);
            console.log('[OfficeDialogUtils] Calling Office.context.ui.messageParent...');
            Office.context.ui.messageParent(message);
            console.log('[OfficeDialogUtils] messageParent called successfully');
        }
        catch (error) {
            console.error('[OfficeDialogUtils] Failed to send message to parent:', error);
        }
    }
}
